import React, { useState, useEffect, useMemo } from 'react';
import './Dashboard.css';
import { loadMonthData, getAllMonthData } from '../utils/storage';
import { COUNTRIES } from './CountrySelector';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const Dashboard = ({ year = new Date().getFullYear() }) => {
  const [stats, setStats] = useState({
    totalViajes: 0,
    totalVuelos: 0,
    totalAlojamientos: 0,
    gastoReal: 0,
    paises: [],
    paisesUnicos: 0,
    proximosViajes: [],
    mesesConViajes: 0
  });

  useEffect(() => {
    calculateStats();
    // Recalcular cuando cambien los datos (cada 2 segundos para detectar cambios)
    const interval = setInterval(calculateStats, 2000);
    return () => clearInterval(interval);
  }, [year]);

  const calculateStats = () => {
    let totalViajes = 0;
    let totalVuelos = 0;
    let totalAlojamientos = 0;
    let gastoReal = 0;
    const paisesSet = new Set();
    const proximosViajes = [];
    let mesesConViajes = 0;

    for (let i = 0; i < 12; i++) {
      const monthData = loadMonthData(i, year);
      
      if (monthData?.plan?.viajes) {
        const viajes = monthData.plan.viajes;
        if (viajes.length > 0) mesesConViajes++;
        
        viajes.forEach(viaje => {
          totalViajes++;
          
          // Países
          if (viaje.pais) {
            paisesSet.add(viaje.pais);
          }
          
          // Transportes (vuelos, trenes, buses)
          const transportes = viaje.transportes || (viaje.vuelos ? viaje.vuelos.map(v => ({ tipo: 'vuelo', ...v })) : []);
          if (transportes && transportes.length > 0) {
            totalVuelos += transportes.length;
          }
          
          // Alojamientos
          if (viaje.airbnbs) {
            totalAlojamientos += viaje.airbnbs.length;
          }
          
          // Próximos viajes
          if (viaje.fechaInicio) {
            const fechaInicio = new Date(viaje.fechaInicio);
            if (fechaInicio >= new Date()) {
              proximosViajes.push({
                destino: viaje.destino || viaje.nombre || 'Sin destino',
                nombre: viaje.nombre,
                pais: viaje.pais,
                fecha: fechaInicio,
                mes: MONTHS[i],
                mesIndex: i
              });
            }
          }
        });
      }
      
      // Presupuesto real de reflection
      if (monthData?.reflection?.presupuesto?.items) {
        monthData.reflection.presupuesto.items.forEach(item => {
          if (item.real) {
            const real = parseFloat(item.real.toString().replace(/[^0-9.,-]/g, '').replace(',', '.')) || 0;
            gastoReal += real;
          }
        });
      }
    }

    // Ordenar próximos viajes por fecha
    proximosViajes.sort((a, b) => a.fecha - b.fecha);

    // Convertir códigos de países a objetos con información completa
    const paisesInfo = Array.from(paisesSet).map(code => {
      const pais = COUNTRIES.find(c => c.code === code);
      return pais || { code, name: code, flag: '' };
    }).sort((a, b) => a.name.localeCompare(b.name, 'es'));

    setStats({
      totalViajes,
      totalVuelos,
      totalAlojamientos,
      gastoReal,
      paises: paisesInfo,
      paisesUnicos: paisesSet.size,
      proximosViajes: proximosViajes.slice(0, 5), // Solo los próximos 5
      mesesConViajes
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short'
    }).format(date);
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="dashboard-title-section">
          <span className="dashboard-icon floating">📊</span>
          <div>
            <h1 className="dashboard-title">Dashboard de Viajes {year}</h1>
            <p className="dashboard-subtitle">Resumen de tu planificación anual</p>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Estadísticas principales */}
        <section className="stats-grid">
          <div className="stat-card pulse-on-hover">
            <div className="stat-icon">✈️</div>
            <div className="stat-content">
              <h3 className="stat-label">Viajes Planificados</h3>
              <p className="stat-value">{stats.totalViajes}</p>
              <p className="stat-description">{stats.mesesConViajes} {stats.mesesConViajes === 1 ? 'mes' : 'meses'} con viajes</p>
            </div>
          </div>

          <div className="stat-card pulse-on-hover">
            <div className="stat-icon">🛫</div>
            <div className="stat-content">
              <h3 className="stat-label">Transportes Reservados</h3>
              <p className="stat-value">{stats.totalVuelos}</p>
              <p className="stat-description">Vuelos, trenes y buses</p>
            </div>
          </div>

          <div className="stat-card pulse-on-hover">
            <div className="stat-icon">🏠</div>
            <div className="stat-content">
              <h3 className="stat-label">Alojamientos</h3>
              <p className="stat-value">{stats.totalAlojamientos}</p>
              <p className="stat-description">Reservas de alojamiento</p>
            </div>
          </div>

          <div className="stat-card pulse-on-hover">
            <div className="stat-icon">💸</div>
            <div className="stat-content">
              <h3 className="stat-label">Gasto Real</h3>
              <p className="stat-value">{formatCurrency(stats.gastoReal)}</p>
              <p className="stat-description">Total registrado</p>
            </div>
          </div>

          <div className="stat-card pulse-on-hover">
            <div className="stat-icon">🗺️</div>
            <div className="stat-content">
              <h3 className="stat-label">Países Visitados</h3>
              <p className="stat-value">{stats.paisesUnicos}</p>
              <p className="stat-description">Países diferentes</p>
            </div>
          </div>
        </section>

        {/* Próximos viajes */}
        {stats.proximosViajes.length > 0 && (
          <section className="upcoming-section">
            <div className="section-title-with-icon">
              <span className="section-icon floating-delayed">📅</span>
              <h2 className="section-title-text">Próximos Viajes</h2>
            </div>
            <div className="upcoming-list">
              {stats.proximosViajes.map((viaje, index) => (
                <div key={index} className="upcoming-card" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="upcoming-date">
                    <span className="date-day">{viaje.fecha.getDate()}</span>
                    <span className="date-month">{formatDate(viaje.fecha).split(' ')[1]}</span>
                  </div>
                  <div className="upcoming-info">
                    <h3 className="upcoming-destination">
                      {viaje.pais && COUNTRIES.find(c => c.code === viaje.pais)?.flag} {viaje.nombre || viaje.destino}
                    </h3>
                    <p className="upcoming-month">{viaje.mes} 2026</p>
                  </div>
                  <div className="upcoming-icon">✈️</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Países Visitados */}
        {stats.paises.length > 0 && (
          <section className="countries-section">
            <div className="section-title-with-icon">
              <span className="section-icon floating-delayed-2">🌎</span>
              <h2 className="section-title-text">Países Planificados</h2>
            </div>
            <div className="countries-grid">
              {stats.paises.map((pais, index) => (
                <div key={pais.code} className="country-card" style={{ animationDelay: `${index * 0.05}s` }}>
                  <div className="country-flag">{pais.flag}</div>
                  <div className="country-name">{pais.name}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Vista mensual resumida */}
        <section className="months-overview-section">
          <div className="section-title-with-icon">
            <span className="section-icon floating-delayed-3">📆</span>
            <h2 className="section-title-text">Resumen Mensual</h2>
          </div>
          <div className="months-grid">
            {MONTHS.map((month, index) => {
              const monthData = loadMonthData(index, year);
              const viajesCount = monthData?.plan?.viajes?.length || 0;
              const hasData = viajesCount > 0 || monthData?.plan?.objetivoViajero || monthData?.reflection;
              
              return (
                <div 
                  key={index} 
                  className={`month-mini-card ${hasData ? 'has-data' : ''}`}
                  style={{ animationDelay: `${index * 0.03}s` }}
                >
                  <div className="month-mini-header">
                    <span className="month-mini-name">{month.substring(0, 3)}</span>
                    {viajesCount > 0 && (
                      <span className="month-mini-badge">{viajesCount}</span>
                    )}
                  </div>
                  <div className="month-mini-content">
                    {hasData ? (
                      <>
                        {viajesCount > 0 && (
                          <div className="month-mini-stat">
                            <span className="mini-icon">✈️</span>
                            <span>{viajesCount} {viajesCount === 1 ? 'viaje' : 'viajes'}</span>
                          </div>
                        )}
                        {monthData?.plan?.objetivoViajero && (
                          <div className="month-mini-stat">
                            <span className="mini-icon">🎯</span>
                            <span>Objetivo</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="month-mini-empty">Sin datos</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;

